import { element } from '../core/dom';
import type { InputDirection, InputState } from '../game/types';

interface InputCallbacks {
  onFire: () => void;
  onPause: () => void;
  onSound: () => void;
  onPointer: (event: PointerEvent) => void;
  onWeaponSlot?: (slot: 0 | 1 | 2) => void;
  onSuspend?: () => void;
}

const KEY_MAP: Partial<Record<string, InputDirection>> = {
  KeyW: 'forward', ArrowUp: 'forward', KeyS: 'back', ArrowDown: 'back',
  KeyA: 'left', ArrowLeft: 'left', KeyD: 'right', ArrowRight: 'right',
};

const INPUT_DIRECTIONS: readonly InputDirection[] = ['forward', 'back', 'left', 'right'];
const ONE_SHOT_KEYS = new Set(['KeyP', 'Escape', 'KeyM']);
const WEAPON_SLOT_KEYS: Partial<Record<string, 0 | 1 | 2>> = {
  Digit1: 0, Numpad1: 0,
  Digit2: 1, Numpad2: 1,
  Digit3: 2, Numpad3: 2,
};

const isModifierShortcut = (event: KeyboardEvent): boolean => event.ctrlKey || event.altKey || event.metaKey;

/**
 * Converts the held movement keys into signed tank control axes.
 *
 * Opposite keys are intentionally subtracted instead of treated as a
 * priority list, so pressing both keys on an axis safely results in zero.
 */
export function readTankControlAxes(input: InputState): { throttle: number; strafe: number } {
  return {
    throttle: Number(input.forward) - Number(input.back),
    // Positive strafe is the tank's local right side.
    strafe: Number(input.right) - Number(input.left),
  };
}

export function weaponSlotForCode(code: string): 0 | 1 | 2 | undefined {
  return WEAPON_SLOT_KEYS[code];
}

export class InputController {
  private readonly pressedOneShotKeys = new Set<string>();
  private pointerFireHeld = false;
  private keyboardFireHeld = false;

  constructor(private readonly input: InputState, private readonly callbacks: InputCallbacks) {
    this.bindEvents();
  }

  private bindEvents(): void {
    window.addEventListener('keydown', (event) => {
      const direction = KEY_MAP[event.code];
      if (direction) {
        if (this.shouldIgnoreKeyboardEvent(event) || isModifierShortcut(event)) {
          // A key can be held while focus moves to a form control. Make sure
          // that transition cannot leave a direction latched in the game.
          if (this.shouldIgnoreKeyboardEvent(event)) {
            this.input[direction] = false;
            this.removeFromOrder(direction);
          }
          return;
        }
        this.input[direction] = true;
        this.removeFromOrder(direction);
        this.ensureOrder().push(direction);
        event.preventDefault();
        return;
      }

      if (event.code === 'Space') {
        if (this.shouldIgnoreKeyboardEvent(event) || isModifierShortcut(event)) return;
        if (!this.keyboardFireHeld) this.callbacks.onFire();
        this.keyboardFireHeld = true;
        this.syncFireState();
        event.preventDefault();
        return;
      }

      const weaponSlot = weaponSlotForCode(event.code);
      if (weaponSlot !== undefined) {
        if (this.shouldIgnoreKeyboardEvent(event) || isModifierShortcut(event)) return;
        if (event.repeat || this.pressedOneShotKeys.has(event.code)) return;
        this.pressedOneShotKeys.add(event.code);
        this.callbacks.onWeaponSlot?.(weaponSlot);
        event.preventDefault();
        return;
      }

      if (!ONE_SHOT_KEYS.has(event.code) || this.shouldIgnoreKeyboardEvent(event) || isModifierShortcut(event)) return;
      // Browsers mark auto-repeat keydown events with repeat=true. The set is
      // also a guard for environments that dispatch duplicate non-repeat events.
      if (event.repeat || this.pressedOneShotKeys.has(event.code)) return;
      this.pressedOneShotKeys.add(event.code);
      event.preventDefault();
      if (event.code === 'KeyP' || event.code === 'Escape') this.callbacks.onPause();
      else if (event.code === 'KeyM') this.callbacks.onSound();
    });
    window.addEventListener('keyup', (event) => {
      this.pressedOneShotKeys.delete(event.code);
      if (event.code === 'Space') {
        this.keyboardFireHeld = false;
        this.syncFireState();
        if (!this.shouldIgnoreKeyboardEvent(event) && !isModifierShortcut(event)) event.preventDefault();
        return;
      }
      const direction = KEY_MAP[event.code];
      if (!direction) return;
      // Always release a direction, even if keyup is delivered to a form
      // control after focus moved away from the game.
      this.input[direction] = false;
      this.removeFromOrder(direction);
      if (!this.shouldIgnoreKeyboardEvent(event) && !isModifierShortcut(event)) event.preventDefault();
    });
    window.addEventListener('blur', this.suspendInput);
    document.addEventListener('visibilitychange', () => {
      this.clearInput();
      if (document.hidden) this.callbacks.onSuspend?.();
    });
    const frame = element<HTMLElement>('#gameFrame');
    frame.addEventListener('pointermove', this.callbacks.onPointer);
    frame.addEventListener('pointerdown', (event) => {
      if ((event.pointerType === 'mouse' && event.button !== 0) || (event.pointerType !== 'mouse' && event.button > 0)) return;
      const target = event.target;
      if ((target instanceof Element && target.closest('button')) || this.isEditableTarget(target)) return;
      this.pointerFireHeld = true;
      this.syncFireState();
      this.callbacks.onPointer(event);
      this.callbacks.onFire();
    });
    window.addEventListener('pointerup', this.releaseFire);
    window.addEventListener('pointercancel', this.releaseFire);
  }

  private readonly clearInput = (): void => {
    INPUT_DIRECTIONS.forEach((direction) => { this.input[direction] = false; });
    this.pointerFireHeld = false;
    this.keyboardFireHeld = false;
    this.syncFireState();
    this.input.order?.splice(0);
    this.pressedOneShotKeys.clear();
  };

  private readonly suspendInput = (): void => {
    this.clearInput();
    this.callbacks.onSuspend?.();
  };

  private readonly releaseFire = (): void => {
    this.pointerFireHeld = false;
    this.syncFireState();
  };

  private syncFireState(): void {
    this.input.fireHeld = this.pointerFireHeld || this.keyboardFireHeld;
  }

  private removeFromOrder(direction: InputDirection): void {
    if (this.input.order) this.input.order = this.input.order.filter((item) => item !== direction);
  }

  private ensureOrder(): InputDirection[] {
    return this.input.order ?? (this.input.order = []);
  }

  private shouldIgnoreKeyboardEvent(event: KeyboardEvent): boolean {
    return this.isEditableTarget(event.target) || this.isEditableTarget(document.activeElement);
  }

  private isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    const interactiveSelector = 'input, select, textarea, button, a[href], [role="button"], [contenteditable]:not([contenteditable="false"])';
    if (target.matches(interactiveSelector)) return true;
    return Boolean(target.closest(interactiveSelector)) ||
      (target as HTMLElement).isContentEditable;
  }
}
